import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import type { ReactNode } from "react";

import appCss from "../styles.css?url";
import thirdPartyInit from "../assets/third-party-init.js?raw";
import { CONSENT_SCRIPT, JSONLD_WEBSITE, JSONLD_ORG } from "@/lib/head-scripts";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { schedulePostHog } from "@/lib/posthog";
import { useLocation, useNavigate } from "@/lib/router-compat";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { CardSetupGuard } from "@/components/dealers/CardSetupGuard";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { UserAccessProvider } from "@/contexts/UserAccessContext";
import Header from "@/shared/Header";
import Footer from "@/shared/Footer";
import { ErrorBoundary } from "@/components/layout/ErrorBoundary";
import { CompareProvider } from "@/contexts/CompareContext";
import { DeferredOptimizers } from "@/components/performance/DeferredOptimizers";
import { useGsapPageTransition } from "@/hooks/useGsapPageTransition";
import { CountryProvider, useCountry } from "@/hooks/useCountry";
import { COUNTRY_ROUTES } from "@/utils/countryRoutes";
import LowercaseRedirect from "@/components/layout/LowercaseRedirect";
import { AutoSEO } from "@/components/seo/AutoSEO";
import NotFound from "@/pages/NotFound";
import "@/lib/installPrompt";

// Non-critical utilities — loaded lazily to reduce main-thread work (ported from App.tsx)
const loadAdvancedAnalytics = () => import("@/utils/advancedAnalytics");
const loadMetaPixel = () => import("@/utils/metaPixel");
const loadBfcache = () => import("@/utils/bfcache");
const loadSpeculationRules = () => import("@/utils/speculationRules");
const loadTrialNotifications = () => import("@/utils/triggerTrialNotifications");

// Lazy overlays (ported from App.tsx)
const CompareFloatingButton = lazy(() => import("@/components/listings/card/CompareFloatingButton").then(m => ({ default: m.CompareFloatingButton })));
const PWAInstallPrompt = lazy(() => import("@/components/pwa/PWAInstallPrompt").then(m => ({ default: m.PWAInstallPrompt })));
const PushNotificationPrompt = lazy(() => import("@/components/notifications/PushNotificationPrompt").then(m => ({ default: m.PushNotificationPrompt })));
const AIAssistant = lazy(() => import("@/components/shared/AIAssistant").then(m => ({ default: m.AIAssistant })));
const CountrySuggestionBanner = lazy(() => import("@/components/layout/CountrySuggestionBanner").then(m => ({ default: m.CountrySuggestionBanner })));
const GDPRBanner = lazy(() => import("@/components/layout/GDPRBanner").then(m => ({ default: m.GDPRBanner })));
const ScrollProgress = lazy(() => import("@/components/layout/ScrollProgress").then(m => ({ default: m.ScrollProgress })));
const BackToTop = lazy(() => import("@/components/layout/BackToTop").then(m => ({ default: m.BackToTop })));
const MobileBottomNav = lazy(() => import("@/components/home/MobileBottomNav"));

const GOOGLE_FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Work+Sans:ital,wght@0,300..700;1,300..700&display=swap";

// Applies the preloaded Google Fonts stylesheet once it lands, so it never
// blocks first paint.
const FONT_SWAP_SCRIPT =
  "(function(){function a(){var l=document.getElementById('gf-css');if(!l)return;if(l.media!=='all')l.media='all';}var l=document.getElementById('gf-css');if(l){l.addEventListener('load',a);}document.addEventListener('DOMContentLoaded',a);window.addEventListener('load',a);setTimeout(a,1200);})();";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({

  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        httpEquiv: "Content-Security-Policy",
        content:
          "default-src 'self' https: data: blob:; script-src 'self' 'unsafe-inline' 'unsafe-eval' https: blob:; style-src 'self' 'unsafe-inline' https:; img-src 'self' data: blob: https:; font-src 'self' data: https:; connect-src 'self' https: wss: data: blob:; media-src 'self' https: data: blob:; frame-src 'self' https:; worker-src 'self' blob:; object-src 'none'; base-uri 'self'; form-action 'self' https:; upgrade-insecure-requests",
      },
      { name: "viewport", content: "width=device-width, initial-scale=1.0, maximum-scale=5.0, minimum-scale=1.0, viewport-fit=cover" },
      { name: "theme-color", content: "#0A1F1C" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "RO Auto Market" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "google-site-verification", content: "EteotXNSTpoXWzR1zw5VhNgNiD7Cues4cF0jO69bWMM" },
      { name: "google-site-verification", content: "g91DhJ-MRLuoTO4KTdxNyoflPWpcdkfGR9tm_7YQrvg" },
      { name: "facebook-domain-verification", content: "m7smxk2jdoaem2spbfhzcskxw4v66p" },
      { name: "msvalidate.01", content: "121757FF6B1F592E8CC48DE42C482F64" },
      { name: "keywords", content: "masini second hand, anunturi auto, masini de vanzare, auto romania, dealeri auto, dacia second hand, volkswagen second hand, bmw second hand" },
      { name: "author", content: "RO Auto Market" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://roautomarket.ro/og-roautomarket.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:locale", content: "ro_RO" },
      { property: "og:locale:alternate", content: "en_GB" },
      { property: "og:locale:alternate", content: "de_DE" },
      { property: "og:site_name", content: "RO Auto Market" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://roautomarket.ro/og-roautomarket.png" },

      { name: "twitter:site", content: "@roautomarket" },
      { name: "twitter:creator", content: "@roautomarket" },
      { name: "verification", content: "3dea3a24b18146a6b85a2e5c2e87ddbb" },
      { name: "msapplication-TileImage", content: "/favicon.png?v=2" },
      { name: "msapplication-TileColor", content: "#0A1F1C" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.json" },
      // hreflang is per-page, not global: these homepage alternates used to be
      // emitted on every route and collided with the per-route alternates, so
      // Google ignored both sets. They now live in src/routes/index.tsx.

      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon.png?v=2" },
      { rel: "shortcut icon", href: "/favicon.png?v=2" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/favicon.png?v=2" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/favicon.png?v=2" },
      { rel: "preconnect", href: "https://ifphnacztbspsexaxssc.supabase.co", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      // Google Fonts loaded non-render-blocking: fetched with high priority via
      // preload, applied once downloaded (media flip in FONT_SWAP_SCRIPT).
      { rel: "preload", as: "style", href: GOOGLE_FONTS_HREF },
      {
        rel: "stylesheet",
        href: GOOGLE_FONTS_HREF,
        media: "print",
        id: "gf-css",
      },


      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://www.google-analytics.com" },
    ],
    scripts: [
      { children: FONT_SWAP_SCRIPT },
      { children: CONSENT_SCRIPT },

      { type: "application/ld+json", children: JSONLD_WEBSITE },
      { type: "application/ld+json", children: JSONLD_ORG },
      // Inline: fișierele din /public sunt servite fără Cache-Control, deci
      // scriptul era re-descărcat la fiecare vizită. Codul se auto-amână
      // (requestIdleCallback), așa că inline nu blochează randarea.
      { children: thirdPartyInit },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="ro" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
          <HelmetProvider>
            <UserAccessProvider>
              <CompareProvider>
                <TooltipProvider>
                  <CountryProvider>
                    <AppShell />
                  </CountryProvider>
                </TooltipProvider>
              </CompareProvider>
            </UserAccessProvider>
          </HelmetProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

// Delayed fallback - only shows after 300ms to avoid flash on fast loads
function SuspenseFallback() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 300);
    return () => clearTimeout(timer);
  }, []);

  if (!show) return null;

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Ported from the pre-migration App.tsx AppContent + main.tsx init code.
function AppShell() {
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const isStartPage = location.pathname === "/start";
  const isPrivateListingFlow = location.pathname.startsWith("/vinde-masina");
  const isIsolatedPage = isStartPage || isPrivateListingFlow;
  const isAuthPage = location.pathname === "/auth" || location.pathname.endsWith("/auth");
  // Color rule: dark homepage (brand showcase); every other public page uses the cream content surface
  const isHomePage = ["/", "/de", "/uk", "/fr"].includes(location.pathname.replace(/\/$/, "") || "/");
  const isAppArea = /^\/(admin|dashboard)(\/|$)/.test(location.pathname);
  const useLightSurface = !isHomePage && !isAppArea;
  const previousPathRef = useRef<string | null>(null);
  const navigate = useNavigate();
  const { country } = useCountry();
  const pageTransitionRef = useGsapPageTransition();

  // ported from main.tsx: manual scroll restoration + auto tracking + i18n preload
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    const idle = (fn: () => void, timeout: number) => {
      if ("requestIdleCallback" in window) requestIdleCallback(fn, { timeout });
      else setTimeout(fn, Math.min(timeout, 4000));
    };
    idle(async () => {
      const { setupAutoTracking } = await import("@/utils/analytics");
      setupAutoTracking();
    }, 5000);
    idle(async () => {
      const { preloadTranslations, prefetchOtherLanguages, getLanguageFromCountry } = await import("@/utils/translations");
      let lang: string = "ro";
      try {
        const stored = window.localStorage.getItem("selectedCountry");
        if (stored) lang = getLanguageFromCountry(stored);
        else {
          const nav = navigator.language;
          if (nav.startsWith("de")) lang = "de";
          else if (nav.startsWith("fr")) lang = "fr";
          else if (nav.toLowerCase().startsWith("en-gb") || nav.toLowerCase() === "en") lang = "en-GB";
        }
      } catch { /* default ro */ }
      await preloadTranslations(lang as never);
      prefetchOtherLanguages(lang as never);
    }, 2000);
    void schedulePostHog();
    idle(() => { loadTrialNotifications(); }, 10000);
  }, []);

  // Handle auth callback tokens from email confirmation - runs on any page
  useEffect(() => {
    const handleAuthTokens = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes("access_token")) {
        const hashParams = new URLSearchParams(hash.substring(1));
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");
        const type = hashParams.get("type");

        if (accessToken && refreshToken) {
          try {
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (!error) {
              const currentPath = window.location.pathname;
              const queryParams = new URLSearchParams(window.location.search);
              const nextPath = queryParams.get("next");
              window.history.replaceState(null, "", `${currentPath}${window.location.search}`);

              if (type === "recovery") {
                navigate("/pricing?tab=reset");
              } else if (nextPath?.startsWith("/")) {
                navigate(nextPath, { replace: true });
              } else if (currentPath.endsWith("/auth/callback")) {
                navigate(COUNTRY_ROUTES.dashboard(country));
              } else {
                navigate(`${currentPath}${window.location.search}`, { replace: true });
              }
            }
          } catch (err) {
            console.error("Auth token handling error:", err);
          }
        }
      }
    };

    handleAuthTokens();
  }, [navigate, country]);

  // Defer all non-critical initialization to idle time to reduce TBT.
  // A no-cache service worker is registered so Android/Chrome can offer install.
  // Register the install service worker as soon as possible: Chrome only fires
  // `beforeinstallprompt` once a SW with a fetch handler is controlling the page.
  useEffect(() => {
    let cancelled = false;
    const register = () => {
      if (cancelled) return;
      import("@/lib/pwa-install").then(({ registerInstallServiceWorker }) => {
        registerInstallServiceWorker();
      });
    };
    if (document.readyState === "complete") register();
    else window.addEventListener("load", register, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener("load", register);
    };
  }, []);

  // FLIP: memorează imaginea cardului pe care se dă click, pentru tranziția
  // către galeria din pagina de detaliu.
  useEffect(() => {
    import("@/lib/flipTransition").then(({ installFlipCapture }) => installFlipCapture());
  }, []);


  useEffect(() => {
    const initNonCritical = async () => {
      const [{ advancedAnalytics }, { initBfcacheOptimizations }, { initDefaultSpeculationRules }] = await Promise.all([
        loadAdvancedAnalytics(),
        loadBfcache(),
        loadSpeculationRules(),
      ]);
      advancedAnalytics.init();
      initBfcacheOptimizations();
      initDefaultSpeculationRules();
    };
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => initNonCritical(), { timeout: 5000 });
    } else {
      setTimeout(() => initNonCritical(), 3000);
    }
  }, []);

  useEffect(() => setMounted(true), []);

  // Overlay-urile flotante (asistent, WhatsApp, GDPR, bottom nav etc.) nu fac
  // parte din conținutul principal: le montăm după `load` + idle, ca să nu
  // concureze cu hidratarea pentru main thread (TBT mai mic pe mobil).
  const [overlaysReady, setOverlaysReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const show = () => {
      if (cancelled) return;
      if ("requestIdleCallback" in window) {
        requestIdleCallback(() => !cancelled && setOverlaysReady(true), { timeout: 3000 });
      } else {
        setTimeout(() => !cancelled && setOverlaysReady(true), 800);
      }
    };
    if (document.readyState === "complete") show();
    else window.addEventListener("load", show, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener("load", show);
    };
  }, []);


  // SPA PageView tracking for Meta Pixel on every route change
  useEffect(() => {
    loadMetaPixel().then(({ trackPageView }) => trackPageView());
    previousPathRef.current = location.pathname;
  }, [location.pathname]);

  return (
    <>
      {overlaysReady && (
        <Suspense fallback={null}>
          <ScrollProgress showOn="detail" />
        </Suspense>
      )}
      <DeferredOptimizers />
      <AutoSEO />
      {mounted && <Toaster />}
      {mounted && <Sonner />}
      <a href="#main-content" className="skip-to-main">
        {country === "DE"
          ? "Zum Hauptinhalt springen"
          : country === "UK"
            ? "Skip to main content"
            : "Sari la conținut principal"}
      </a>

      <ScrollToTop />
      {overlaysReady && <Suspense fallback={null}><BackToTop /></Suspense>}
      {!isIsolatedPage && <Header />}

      {!isIsolatedPage && overlaysReady && <Suspense fallback={null}><CountrySuggestionBanner /></Suspense>}
      <main id="main-content" ref={pageTransitionRef} className={`${isIsolatedPage ? "" : "pb-20"}${useLightSurface ? " surface-light" : ""}`} role="main" aria-label="Conținut principal">
        <CardSetupGuard>
          <Suspense fallback={<SuspenseFallback />}>
            <LowercaseRedirect />
            <Outlet />
          </Suspense>
        </CardSetupGuard>
      </main>
      {!isIsolatedPage && <Footer />}
      {/* Spațiu ca butonul „Asistent” și bara mobilă să nu acopere ultimul element */}
      {!isIsolatedPage && <div aria-hidden="true" className="h-32 md:h-20" style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }} />}
      {!isIsolatedPage && overlaysReady && (
        <Suspense fallback={null}>
          <CompareFloatingButton />
          {!isAuthPage && <PWAInstallPrompt />}
          {!isAuthPage && <PushNotificationPrompt />}
          <AIAssistant showLauncher={!hideAssistantLauncher(location.pathname)} />
          <GDPRBanner />
          {!location.pathname.startsWith('/listing/') && <MobileBottomNav />}
        </Suspense>

      )}
    </>
  );
}

// Pagini unde butonul „Asistent" nu apare: anunț (are bara vânzătorului),
// liste și categorii (au căutarea descriptivă), formulare și plăți.
const LAUNCHER_HIDDEN = [
  /^\/listing\//, /^\/vehicles/, /^\/cars/, /^\/masini-second-hand/,
  /^\/(camioane|ambarcatiuni|utilaje|motociclete|autoutilitare|anunturi|cauta)(\/|$)/,
  /^\/vinde-masina/, /^\/dealeri\/?$/, /^\/pricing/, /^\/preturi/, /auth/,
  /checkout/, /^\/activate-card/, /^\/create-listing/, /^\/create-dealer/, /^\/account\/billing/, /^\/dashboard\/billing/,
];
function hideAssistantLauncher(pathname: string) {
  return LAUNCHER_HIDDEN.some((r) => r.test(pathname));
}

function RootErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const err = error instanceof Error ? error : new Error(String(error));
  useEffect(() => {
    reportLovableError(err, { boundary: "tanstack_root_error_component" });
  }, [err]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6">
      <div className="max-w-md text-center space-y-4">
        <h1 className="text-2xl font-bold">Această pagină nu s-a încărcat</h1>
        <p className="text-muted-foreground">A apărut o eroare neașteptată. Încearcă din nou sau revino la pagina principală.</p>
        <div className="flex items-center justify-center gap-3">
          <button
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Încearcă din nou
          </button>
          <a href="/" className="px-4 py-2 rounded-md border border-border">
            Pagina principală
          </a>
        </div>
      </div>
    </div>
  );
}
